/**
 * PATH: apps/quote/src/components/builder/scope-ag-grid/ScopeAgGridEditor.tsx
 * FILE_ID: QB-AGGRID-EDITOR-001
 * PURPOSE: Orchestrator that mounts ONE ScopeAgGridSection at a time,
 *          chosen by the SAM-style ScopeAgGridTabStrip across the top
 *          of the editor. Replaces the legacy "stack every section
 *          collapsed underneath each other" view with a tabbed view
 *          per SAM's quote-workbook UX.
 * WHY: SAM's quote workbook has a LEGACY IMPORT / + Add tab strip.
 *      Estimators expect one sheet at a time, not an accordion of
 *      every section. The Quotech data model already supports it
 *      (Quote → sections[] → items[]) — we just had no tabbed
 *      navigation UI until now.
 * RELATED:
 *   - QB-AGGRID-TABSTRIP-001 (ScopeAgGridTabStrip) — tab UI
 *   - QB-AGGRID-SECTION-001 (ScopeAgGridSection) — per-section grid
 *   - apps/quote/src/components/builder/ScopeSheetGrid.tsx — legacy fallback
 */

import { useEffect, useState } from 'react'
import { Layers, Plus } from 'lucide-react'
import type { QuoteSection, QuoteLineItem } from '@/lib/quoteApi'
import type { CustomColumnDef } from '../scope-sheet-grid/ScopeSheetGridTypes'
import ScopeAgGridSection from './ScopeAgGridSection'
import ScopeAgGridTabStrip from './ScopeAgGridTabStrip'

interface Props {
  sections: QuoteSection[]
  isEditable: boolean
  visibleOptionalIds: string[]
  customColumns: CustomColumnDef[]
  configDefaults: {
    defaultProductivityPmpd?: number | string | null
    defaultUnit?: string
  }
  onAddItem: (
    sectionId: string,
    data: {
      location?: string
      description: string
      quantity: number
      unit: string
      productivity_pmpd: number
    },
  ) => void
  onUpdateItem: (
    sectionId: string,
    itemId: string,
    patch: Partial<QuoteLineItem>,
  ) => void
  onDeleteItem: (sectionId: string, itemId: string) => void
  onAddSection: (title: string) => void
  onPasteWithCreate?: (
    sectionId: string,
    existingItems: QuoteLineItem[],
    pasteData: Array<Record<string, unknown>>,
    startRow: number,
  ) => Promise<void>
}

/**
 * Pick the next active section id when the section list changes.
 * Preserves the current pick if it still exists, otherwise falls back
 * to the first available section's id (or null when empty).
 */
function resolveActiveSectionId(
  sections: QuoteSection[],
  current: string | null,
): string | null {
  if (current && sections.some((s) => s.id === current)) return current
  return sections[0]?.id ?? null
}

export default function ScopeAgGridEditor({
  sections,
  isEditable,
  visibleOptionalIds,
  customColumns,
  configDefaults,
  onAddItem,
  onUpdateItem,
  onDeleteItem,
  onAddSection,
  onPasteWithCreate,
}: Props) {
  const [activeSectionId, setActiveSectionId] = useState<string | null>(() =>
    resolveActiveSectionId(sections, null),
  )

  // Sections can change underneath us (server refetch, another agent's
  // edit). Keep the active id valid; default to the first when our
  // current pick disappears.
  useEffect(() => {
    setActiveSectionId((cur) => resolveActiveSectionId(sections, cur))
  }, [sections])

  const activeSection = sections.find((s) => s.id === activeSectionId) ?? null

  if (sections.length === 0) {
    return (
      <div className="flex flex-col gap-3 h-full min-h-0 p-2">
        <div className="flex flex-col items-center justify-center h-64 text-center gap-3">
          <Layers className="w-12 h-12 text-slate-300 dark:text-slate-600" />
          <h3 className="text-lg font-medium text-slate-900 dark:text-white">
            Start building your quote
          </h3>
          <p className="text-sm text-slate-600 dark:text-slate-400 max-w-sm">
            Add a sheet (e.g. &ldquo;Scope of Works&rdquo;, &ldquo;External Walls&rdquo;)
            and start entering line items. Tab/Enter to navigate, paste
            from Excel works out of the box.
          </p>
          {isEditable && (
            <button
              onClick={() => onAddSection('Scope of Works')}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-600 text-white font-medium hover:bg-amber-700"
            >
              <Plus className="w-4 h-4" />
              Add first sheet
            </button>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      <ScopeAgGridTabStrip
        sections={sections}
        activeSectionId={activeSectionId}
        isEditable={isEditable}
        onActivate={setActiveSectionId}
        onAdd={() => onAddSection('New sheet')}
      />
      <div className="flex-1 min-h-0 p-2" role="tabpanel">
        {activeSection && (
          <ScopeAgGridSection
            key={activeSection.id}
            section={activeSection}
            isEditable={isEditable}
            visibleOptionalIds={visibleOptionalIds}
            customColumns={customColumns}
            configDefaults={configDefaults}
            onAddItem={onAddItem}
            onUpdateItem={onUpdateItem}
            onDeleteItem={onDeleteItem}
            onAddSection={() => onAddSection('New sheet')}
            onPasteWithCreate={onPasteWithCreate}
          />
        )}
      </div>
    </div>
  )
}

// Exported for unit tests — the pure I→O picker is the heart of the
// "preserve active tab across refetches" behaviour.
export { resolveActiveSectionId }
